import { useEffect, useState, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ExportButtons } from '@/components/ExportButtons';;
import PageTour from '@/components/PageTour';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, Users, MapPin, AlertTriangle, CheckCircle2, LogIn, LogOut, Timer, BarChart3, TrendingUp, TrendingDown, Flame, Award, Building2, CalendarDays, Bell, BellRing, XCircle, RefreshCw, Eye } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

// Mini bar chart component (pure CSS)
function MiniBar({ value, max, color = 'bg-info', height = 'h-24' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className={`w-full ${height} flex items-end`}>
      <div className={`w-full ${color} rounded-t transition-all duration-500`} style={{ height: `${pct}%`, minHeight: value > 0 ? '4px' : '0' }} />
    </div>
  );
}

// Horizontal bar for rankings
function HBar({ value, max, color = 'bg-info', label }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-3 group">
      <span className="text-xs text-muted-foreground dark:text-muted-foreground w-28 truncate font-medium" title={label}>{label}</span>
      <div className="flex-1 h-5 bg-muted dark:bg-muted rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs font-bold text-foreground dark:text-muted-foreground w-10 text-right">{value}</span>
    </div>
  );
}

// Heatmap cell
function HeatCell({ value, max }) {
  const intensity = max > 0 ? Math.min(value / max, 1) : 0;
  const bg = intensity === 0 ? 'bg-muted dark:bg-muted' :
    intensity < 0.3 ? 'bg-success/20 dark:bg-emerald-900/40' :
    intensity < 0.6 ? 'bg-emerald-300 dark:bg-emerald-700/60' :
    intensity < 0.85 ? 'bg-success dark:bg-success/70' :
    'bg-emerald-700 dark:bg-emerald-300';
  return (
    <div className={`w-10 h-7 ${bg} rounded text-[10px] flex items-center justify-center font-mono text-foreground dark:text-stone-200 transition-all`} title={`${value}h`}>
      {value > 0 ? value : ''}
    </div>
  );
}

export default function AttendanceDashboardPage() {
  const { t } = useLanguage();
  const [tab, setTab] = useState('today');
  const [todayData, setTodayData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [attendanceHistory, setAttendanceHistory] = useState({ data: [], total: 0 });
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [clockDialog, setClockDialog] = useState(null);
  const [selectedEmp, setSelectedEmp] = useState('');
  const [page, setPage] = useState(1);
  const [filterEmp, setFilterEmp] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [analyticsDays, setAnalyticsDays] = useState('30');
  const [analyticsBranch, setAnalyticsBranch] = useState('');
  const [branches, setBranches] = useState([]);
  const [alerts, setAlerts] = useState({ alerts: [], stats: { total: 0, active: 0 } });
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertDays, setAlertDays] = useState('7');
  const [alertFilter, setAlertFilter] = useState('active');

  const fetchData = async () => {
    try {
      const [todayRes, empRes, histRes, branchRes] = await Promise.allSettled([
        api.get('/attendance/today'),
        api.get('/employees'),
        api.get('/attendance', { params: { page, limit: 30, employee_id: filterEmp || undefined, start_date: filterDate || undefined } }),
        api.get('/branches'),
      ]);
      if (todayRes.status === 'fulfilled' && todayRes.value?.data) setTodayData(todayRes.value.data);
      if (empRes.status === 'fulfilled' && Array.isArray(empRes.value?.data)) {
        setEmployees(empRes.value.data.filter(e => e.status === 'active' || e.active || e.is_active));
      }
      if (histRes.status === 'fulfilled' && histRes.value?.data) setAttendanceHistory(histRes.value.data);
      if (branchRes.status === 'fulfilled') {
        const bd = branchRes.value?.data;
        setBranches(Array.isArray(bd) ? bd : Array.isArray(bd?.data) ? bd.data : []);
      }
      if (todayRes.status === 'rejected' && empRes.status === 'rejected') {
        toast.error('Failed to load attendance data');
      }
    } catch (err) {
      console.error('fetchData error:', err);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api.get('/attendance/analytics', { params: { days: parseInt(analyticsDays), branch_id: analyticsBranch || undefined } });
      setAnalytics(res.data);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchAlerts = async () => {
    setAlertsLoading(true);
    try {
      const res = await api.get('/attendance/alerts', { params: { days: parseInt(alertDays), status: alertFilter === 'all' ? undefined : alertFilter } });
      setAlerts(res.data);
    } catch { } finally { setAlertsLoading(false); }
  };

  const dismissAlert = async (id) => {
    try {
      await api.put(`/attendance/alerts/${id}/dismiss`);
      toast.success('Alert dismissed');
      fetchAlerts();
    } catch { toast.error('Failed to dismiss'); }
  };

  const dismissAll = async () => {
    try {
      const res = await api.post('/attendance/alerts/dismiss-all');
      toast.success(res.data.message);
      fetchAlerts();
    } catch { toast.error('Failed'); }
  };

  const runAlertCheck = async () => {
    try {
      const res = await api.post('/attendance/alerts/check-now');
      toast.success(res.data.message);
      fetchAlerts();
    } catch { toast.error('Failed to run check'); }
  };

  useEffect(() => { fetchData(); }, [page, filterEmp, filterDate]);
  useEffect(() => { if (tab === 'analytics') fetchAnalytics(); }, [tab, analyticsDays, analyticsBranch]);
  useEffect(() => { if (tab === 'alerts') fetchAlerts(); }, [tab, alertDays, alertFilter]);

  const handleClockIn = async () => {
    if (!selectedEmp) { toast.error('Select an employee'); return; }
    try {
      await api.post('/attendance/clock-in', { employee_id: selectedEmp });
      toast.success('Clocked in successfully');
      setClockDialog(null); setSelectedEmp(''); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clock in'); }
  };

  const handleClockOut = async () => {
    if (!selectedEmp) { toast.error('Select an employee'); return; }
    try {
      const res = await api.post('/attendance/clock-out', { employee_id: selectedEmp });
      toast.success(`Clocked out - ${res.data.total_hours}h worked`);
      setClockDialog(null); setSelectedEmp(''); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to clock out'); }
  };

  const records = todayData?.records || [];
  const activeEmps = employees;

  // Trend chart dimensions
  const trendMax = useMemo(() => {
    if (!analytics?.trend) return 1;
    return Math.max(...analytics.trend.map(d => d.present + d.late + d.absent), 1);
  }, [analytics]);

  const kpis = analytics?.kpis || {};

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="attendance-dashboard-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200/50">
                <Clock size={24} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground dark:text-white" data-testid="page-title">{t('attendance_title') || 'Attendance Dashboard'}</h1>
                <p className="text-sm text-muted-foreground mt-0.5">Track attendance, overtime, and patterns.</p>
              
            <ExportButtons dataType="attendance" label="Attendance" /></div>
            </div>
          </div>
          <div className="flex gap-2">
            <PageTour storageKey="tour_attendance" steps={[
              { target: '[data-testid="clock-in-btn"]', title: 'Clock In', content: 'Record employee check-in time. Select the employee and confirm.' },
              { target: '[data-testid="clock-out-btn"]', title: 'Clock Out', content: 'Record employee departure. Overtime is calculated automatically.' },
              { target: '[data-testid="tab-analytics"]', title: 'Attendance Analytics', content: 'View attendance trends, patterns, and generate reports over any period.' },
            ]} />
            <Button onClick={() => { setClockDialog('in'); setSelectedEmp(''); }} data-testid="clock-in-btn-2">
              <LogIn size={14} className="mr-1.5" /> Clock In
            </Button>
            <Button variant="outline" onClick={() => { setClockDialog('out'); setSelectedEmp(''); }} data-testid="clock-out-btn-2">
              <LogOut size={14} className="mr-1.5" /> Clock Out
            </Button>
          </div>
        </div>

        {/* Today KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card data-testid="stat-total"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-info/10 dark:bg-blue-900/30 rounded-lg"><Users size={18} className="text-info" /></div><div><div className="text-2xl font-bold dark:text-white">{todayData?.total_employees || 0}</div><div className="text-xs text-muted-foreground">Total Employees</div></div></div></CardContent></Card>
          <Card data-testid="stat-present"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-success/10 dark:bg-emerald-900/30 rounded-lg"><CheckCircle2 size={18} className="text-success" /></div><div><div className="text-2xl font-bold dark:text-white">{todayData?.present || 0}</div><div className="text-xs text-muted-foreground">Present</div></div></div></CardContent></Card>
          <Card data-testid="stat-late"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-warning/10 dark:bg-amber-900/30 rounded-lg"><AlertTriangle size={18} className="text-warning" /></div><div><div className="text-2xl font-bold dark:text-white">{todayData?.late || 0}</div><div className="text-xs text-muted-foreground">Late</div></div></div></CardContent></Card>
          <Card data-testid="stat-absent"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-error/10 dark:bg-red-900/30 rounded-lg"><Users size={18} className="text-error" /></div><div><div className="text-2xl font-bold dark:text-white">{todayData?.absent || 0}</div><div className="text-xs text-muted-foreground">Absent</div></div></div></CardContent></Card>
          <Card data-testid="stat-still-in"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-purple-50 dark:bg-purple-900/30 rounded-lg"><Timer size={18} className="text-purple-600" /></div><div><div className="text-2xl font-bold dark:text-white">{todayData?.still_clocked_in || 0}</div><div className="text-xs text-muted-foreground">Still In</div></div></div></CardContent></Card>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList data-testid="attendance-tabs">
            <TabsTrigger value="today" data-testid="tab-today">Today's Log</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics-2"><BarChart3 size={14} className="mr-1.5" />Analytics</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts"><Bell size={14} className="mr-1.5" />Alerts{alerts.stats?.active > 0 && <Badge variant="destructive" className="ml-1.5 text-[10px] h-4 px-1.5">{alerts.stats.active}</Badge>}</TabsTrigger>
          </TabsList>

          {/* TODAY TAB */}
          <TabsContent value="today">
            <Card>
              <CardHeader><CardTitle className="text-base">Today - {todayData?.date}</CardTitle></CardHeader>
              <CardContent>
                {records.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">No attendance records for today yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="today-table">
                      <thead><tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="pb-2 pr-4">Employee</th><th className="pb-2 pr-4">Clock In</th><th className="pb-2 pr-4">Clock Out</th><th className="pb-2 pr-4">Hours</th><th className="pb-2 pr-4">Overtime</th><th className="pb-2 pr-4">Late (min)</th><th className="pb-2">Status</th>
                      </tr></thead>
                      <tbody>
                        {records.map((r) => (
                          <tr key={r.id} className="border-b last:border-0 hover:bg-muted/50 dark:hover:bg-muted/50" data-testid={`att-row-${r.id}`}>
                            <td className="py-2.5 pr-4 font-medium dark:text-stone-200">{r.employee_name}</td>
                            <td className="py-2.5 pr-4 text-xs">{r.clock_in_time || '-'}</td>
                            <td className="py-2.5 pr-4 text-xs">{r.clock_out_time || <Badge variant="outline" className="text-[10px]">Active</Badge>}</td>
                            <td className="py-2.5 pr-4">{r.total_hours || '-'}</td>
                            <td className="py-2.5 pr-4">{r.overtime_hours > 0 ? <span className="text-success font-medium">{r.overtime_hours}h</span> : '-'}</td>
                            <td className="py-2.5 pr-4">{r.late_minutes > 0 ? <span className="text-error">{r.late_minutes}</span> : '-'}</td>
                            <td className="py-2.5"><Badge variant={r.status === 'present' ? 'default' : r.status === 'late' ? 'destructive' : 'secondary'} className="text-xs">{r.status}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* HISTORY TAB */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-4">
                  <CardTitle className="text-base">Attendance History</CardTitle>
                  <Select value={filterEmp || 'all'} onValueChange={(v) => { setFilterEmp(v === 'all' ? '' : v); setPage(1); }}>
                    <SelectTrigger className="w-48" data-testid="filter-emp"><SelectValue placeholder="All Employees" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Employees</SelectItem>{activeEmps.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="date" className="w-40" value={filterDate} onChange={(e) => { setFilterDate(e.target.value); setPage(1); }} data-testid="filter-date" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="history-table">
                    <thead><tr className="border-b text-left text-xs text-muted-foreground">
                      <th className="pb-2 pr-4">Date</th><th className="pb-2 pr-4">Employee</th><th className="pb-2 pr-4">In</th><th className="pb-2 pr-4">Out</th><th className="pb-2 pr-4">Hours</th><th className="pb-2 pr-4">OT</th><th className="pb-2">Status</th>
                    </tr></thead>
                    <tbody>
                      {(attendanceHistory.data || []).map((r) => (
                        <tr key={r.id} className="border-b last:border-0 hover:bg-muted/50 dark:hover:bg-muted/50">
                          <td className="py-2 pr-4 dark:text-muted-foreground">{r.date}</td>
                          <td className="py-2 pr-4 font-medium dark:text-stone-200">{r.employee_name}</td>
                          <td className="py-2 pr-4 text-xs">{r.clock_in_time || '-'}</td>
                          <td className="py-2 pr-4 text-xs">{r.clock_out_time || '-'}</td>
                          <td className="py-2 pr-4">{r.total_hours || '-'}</td>
                          <td className="py-2 pr-4">{r.overtime_hours > 0 ? `${r.overtime_hours}h` : '-'}</td>
                          <td className="py-2"><Badge variant={r.status === 'present' ? 'default' : r.status === 'late' ? 'destructive' : 'secondary'} className="text-[10px]">{r.status}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {attendanceHistory.total > 30 && (
                  <div className="flex justify-center gap-2 mt-4">
                    <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                    <span className="text-sm text-muted-foreground py-1.5">Page {page} of {attendanceHistory.pages || 1}</span>
                    <Button size="sm" variant="outline" disabled={page >= (attendanceHistory.pages || 1)} onClick={() => setPage(page + 1)}>Next</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ANALYTICS TAB */}
          <TabsContent value="analytics">
            <div className="space-y-5" data-testid="analytics-section">
              {/* Filters */}
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={analyticsDays} onValueChange={setAnalyticsDays}>
                  <SelectTrigger className="w-36" data-testid="analytics-days-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="14">Last 14 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                    <SelectItem value="180">Last 6 months</SelectItem>
                    <SelectItem value="365">Last year</SelectItem>
                  </SelectContent>
                </Select>
                {branches.length > 0 && (
                  <Select value={analyticsBranch || '__all__'} onValueChange={(v) => setAnalyticsBranch(v === '__all__' ? '' : v)}>
                    <SelectTrigger className="w-44" data-testid="analytics-branch-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Branches</SelectItem>
                      {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {analyticsLoading ? (
                <div className="text-center py-12 text-muted-foreground"><Clock size={24} className="animate-spin mx-auto mb-2" />Loading analytics...</div>
              ) : analytics ? (
                <>
                  {/* KPI Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="analytics-kpis">
                    <Card className="border-l-4 border-l-blue-500">
                      <CardContent className="pt-3 pb-2">
                        <div className="text-xs text-muted-foreground mb-1">Avg Hours/Day</div>
                        <div className="text-xl font-bold dark:text-white" data-testid="kpi-avg-hours">{kpis.avg_hours_per_day || 0}</div>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-emerald-500">
                      <CardContent className="pt-3 pb-2">
                        <div className="text-xs text-muted-foreground mb-1">On-Time Rate</div>
                        <div className="text-xl font-bold dark:text-white" data-testid="kpi-ontime-rate">{kpis.on_time_rate || 100}%</div>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-amber-500">
                      <CardContent className="pt-3 pb-2">
                        <div className="text-xs text-muted-foreground mb-1">Total Late</div>
                        <div className="text-xl font-bold dark:text-white" data-testid="kpi-total-late">{kpis.total_late || 0}</div>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-violet-500">
                      <CardContent className="pt-3 pb-2">
                        <div className="text-xs text-muted-foreground mb-1">Overtime (hrs)</div>
                        <div className="text-xl font-bold dark:text-white" data-testid="kpi-overtime">{kpis.total_overtime_hours || 0}</div>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-rose-500">
                      <CardContent className="pt-3 pb-2">
                        <div className="text-xs text-muted-foreground mb-1">Today Absent</div>
                        <div className="text-xl font-bold dark:text-white" data-testid="kpi-today-absent">{kpis.today_absent || 0}</div>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-cyan-500">
                      <CardContent className="pt-3 pb-2">
                        <div className="text-xs text-muted-foreground mb-1">Total Records</div>
                        <div className="text-xl font-bold dark:text-white" data-testid="kpi-total-records">{kpis.total_records || 0}</div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Attendance Trend Chart */}
                  <Card data-testid="trend-chart-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-outfit flex items-center gap-2">
                        <TrendingUp size={16} className="text-info" />
                        Attendance Trend
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-end gap-[2px] h-32 overflow-x-auto" data-testid="trend-chart">
                        {(analytics.trend || []).map((d, i) => {
                          const total = d.present + d.late + d.absent;
                          const pctPresent = trendMax > 0 ? (d.present / trendMax) * 100 : 0;
                          const pctLate = trendMax > 0 ? (d.late / trendMax) * 100 : 0;
                          const pctAbsent = trendMax > 0 ? (d.absent / trendMax) * 100 : 0;
                          return (
                            <div key={d.date} className="flex-1 min-w-[6px] flex flex-col items-stretch justify-end h-full group relative" title={`${d.date}: ${d.present} on-time, ${d.late} late, ${d.absent} absent`}>
                              <div className="bg-red-300 dark:bg-red-700 rounded-t-[1px]" style={{ height: `${pctAbsent}%`, minHeight: d.absent > 0 ? '2px' : 0 }} />
                              <div className="bg-amber-400 dark:bg-warning" style={{ height: `${pctLate}%`, minHeight: d.late > 0 ? '2px' : 0 }} />
                              <div className="bg-success dark:bg-success rounded-b-[1px]" style={{ height: `${pctPresent}%`, minHeight: d.present > 0 ? '2px' : 0 }} />
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-4 mt-3 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-success inline-block" /> On-time</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400 inline-block" /> Late</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-300 inline-block" /> Absent</span>
                        <span className="ml-auto">{analyticsDays} days</span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Rankings Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Late Arrivals */}
                    <Card data-testid="late-ranking-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-outfit flex items-center gap-2">
                          <AlertTriangle size={16} className="text-warning" />
                          Most Late Arrivals
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {(analytics.late_ranking || []).length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-4">No late arrivals in this period</p>
                        ) : (
                          analytics.late_ranking.map((e, i) => (
                            <HBar key={e.id} value={e.count} max={analytics.late_ranking[0]?.count || 1} color="bg-amber-400" label={e.name} />
                          ))
                        )}
                      </CardContent>
                    </Card>

                    {/* Absenteeism */}
                    <Card data-testid="absent-ranking-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-outfit flex items-center gap-2">
                          <TrendingDown size={16} className="text-error" />
                          Most Absences
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {(analytics.absent_ranking || []).length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-4">No absences in this period</p>
                        ) : (
                          analytics.absent_ranking.map((e, i) => (
                            <HBar key={e.id} value={e.count} max={analytics.absent_ranking[0]?.count || 1} color="bg-red-400" label={e.name} />
                          ))
                        )}
                      </CardContent>
                    </Card>

                    {/* Overtime */}
                    <Card data-testid="overtime-ranking-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-outfit flex items-center gap-2">
                          <Flame size={16} className="text-violet-500" />
                          Top Overtime
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {(analytics.overtime_ranking || []).length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-4">No overtime in this period</p>
                        ) : (
                          analytics.overtime_ranking.map((e, i) => (
                            <HBar key={e.id} value={e.hours} max={analytics.overtime_ranking[0]?.hours || 1} color="bg-violet-400" label={e.name} />
                          ))
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Hours Heatmap */}
                  <Card data-testid="heatmap-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-outfit flex items-center gap-2">
                        <CalendarDays size={16} className="text-success" />
                        Weekly Hours Heatmap (Last 4 Weeks)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {(analytics.heatmap || []).length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-4">No data for heatmap</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="text-xs">
                            <thead>
                              <tr>
                                <th className="text-left pb-2 pr-4 text-muted-foreground font-medium w-28">Employee</th>
                                <th className="pb-2 px-1 text-muted-foreground font-medium">W-4</th>
                                <th className="pb-2 px-1 text-muted-foreground font-medium">W-3</th>
                                <th className="pb-2 px-1 text-muted-foreground font-medium">W-2</th>
                                <th className="pb-2 px-1 text-muted-foreground font-medium">W-1</th>
                              </tr>
                            </thead>
                            <tbody>
                              {analytics.heatmap.map((row) => {
                                const maxH = Math.max(...analytics.heatmap.flatMap(r => r.weeks), 1);
                                return (
                                  <tr key={row.id}>
                                    <td className="py-0.5 pr-4 text-muted-foreground dark:text-muted-foreground truncate max-w-[120px]" title={row.name}>{row.name}</td>
                                    {row.weeks.map((h, wi) => <td key={wi} className="py-0.5 px-1"><HeatCell value={h} max={maxH} /></td>)}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Branch Comparison */}
                  {(analytics.branch_comparison || []).length > 1 && (
                    <Card data-testid="branch-comparison-card">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-outfit flex items-center gap-2">
                          <Building2 size={16} className="text-info" />
                          Branch Comparison
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {analytics.branch_comparison.map((b) => (
                            <div key={b.id} className="p-3 border rounded-xl dark:border-stone-700" data-testid={`branch-stat-${b.id}`}>
                              <p className="text-sm font-semibold dark:text-white mb-2">{b.name}</p>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-success">{b.present} on-time</span>
                                <span className="text-warning">{b.late} late</span>
                                <span className="ml-auto font-bold text-foreground dark:text-muted-foreground">{b.rate}%</span>
                              </div>
                              <div className="mt-2 h-2 bg-muted dark:bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-success rounded-full" style={{ width: `${b.rate}%` }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : null}
            </div>
          </TabsContent>

          {/* ALERTS TAB */}
          <TabsContent value="alerts">
            <div className="space-y-4" data-testid="alerts-section">
              {/* Alert Controls */}
              <div className="flex items-center gap-3 flex-wrap">
                <Select value={alertFilter} onValueChange={setAlertFilter}>
                  <SelectTrigger className="w-32" data-testid="alert-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="dismissed">Dismissed</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={alertDays} onValueChange={setAlertDays}>
                  <SelectTrigger className="w-32" data-testid="alert-days-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Today</SelectItem>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={runAlertCheck} data-testid="run-alert-check-btn"><RefreshCw size={14} className="mr-1.5" />Check Now</Button>
                {alerts.stats?.active > 0 && <Button variant="outline" size="sm" className="text-error border-error/30" onClick={dismissAll} data-testid="dismiss-all-btn"><XCircle size={14} className="mr-1.5" />Dismiss All</Button>}
              </div>

              {/* Alert Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Total Alerts</div><div className="text-xl font-bold dark:text-white" data-testid="alert-stat-total">{alerts.stats?.total || 0}</div></CardContent></Card>
                <Card className="border-l-4 border-l-amber-400"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Active</div><div className="text-xl font-bold text-warning" data-testid="alert-stat-active">{alerts.stats?.active || 0}</div></CardContent></Card>
                <Card><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Late</div><div className="text-xl font-bold dark:text-white" data-testid="alert-stat-late">{alerts.stats?.by_type?.late || 0}</div></CardContent></Card>
                <Card><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Absent</div><div className="text-xl font-bold dark:text-white" data-testid="alert-stat-absent">{alerts.stats?.by_type?.absent || 0}</div></CardContent></Card>
                <Card className="border-l-4 border-l-red-400"><CardContent className="pt-3 pb-2"><div className="text-xs text-muted-foreground">Consecutive</div><div className="text-xl font-bold dark:text-white" data-testid="alert-stat-consecutive">{alerts.stats?.by_type?.consecutive_absent || 0}</div></CardContent></Card>
              </div>

              {/* Alert List */}
              <Card data-testid="alerts-list-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-outfit flex items-center gap-2">
                    <BellRing size={16} className="text-warning" />
                    Alerts ({alerts.alerts?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {alertsLoading ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">Loading alerts...</div>
                  ) : (alerts.alerts || []).length === 0 ? (
                    <div className="text-center py-12">
                      <CheckCircle2 size={32} className="mx-auto text-emerald-400 mb-2" />
                      <p className="text-sm text-muted-foreground">No alerts found</p>
                      <p className="text-xs text-muted-foreground mt-1">All clear! Configure alert rules in Settings &gt; Attendance.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {alerts.alerts.map((a) => (
                        <div key={a.id} className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${
                          a.status === 'dismissed' ? 'opacity-50 border-border dark:border-stone-700 bg-muted/50 dark:bg-muted/50' :
                          a.severity === 'critical' ? 'border-error/30 bg-error/10 dark:border-red-800 dark:bg-red-900/10' :
                          'border-warning/30 bg-warning/10 dark:border-amber-800 dark:bg-amber-900/10'
                        }`} data-testid={`alert-item-${a.id}`}>
                          <div className={`p-1.5 rounded-lg mt-0.5 ${
                            a.alert_type === 'late' ? 'bg-warning/20 dark:bg-amber-900/30' :
                            a.alert_type === 'consecutive_absent' ? 'bg-error/20 dark:bg-red-900/30' :
                            'bg-orange-100 dark:bg-orange-900/30'
                          }`}>
                            {a.alert_type === 'late' ? <Clock size={14} className="text-warning" /> :
                             a.alert_type === 'consecutive_absent' ? <AlertTriangle size={14} className="text-error" /> :
                             <XCircle size={14} className="text-orange-600" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-sm font-semibold dark:text-white">{a.employee_name}</span>
                              <Badge variant={a.severity === 'critical' ? 'destructive' : 'secondary'} className="text-[10px]">{a.alert_type?.replace('_', ' ')}</Badge>
                              {a.status === 'dismissed' && <Badge variant="outline" className="text-[10px]">Dismissed</Badge>}
                            </div>
                            <p className="text-xs text-muted-foreground dark:text-muted-foreground">{a.message}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">{a.date} &middot; {new Date(a.created_at).toLocaleTimeString()}</p>
                          </div>
                          {a.status === 'active' && (
                            <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground hover:text-muted-foreground" onClick={() => dismissAlert(a.id)} data-testid={`dismiss-alert-${a.id}`}>
                              <Eye size={14} />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        <Dialog open={!!clockDialog} onOpenChange={() => setClockDialog(null)}>
          <DialogContent data-testid="clock-dialog">
            <DialogHeader><DialogTitle>{clockDialog === 'in' ? 'Clock In' : 'Clock Out'} Employee</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Employee</Label>
                <Select value={selectedEmp || 'none'} onValueChange={(v) => setSelectedEmp(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="clock-emp-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>{activeEmps.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={clockDialog === 'in' ? handleClockIn : handleClockOut} className="w-full" data-testid="clock-submit-btn">
                {clockDialog === 'in' ? 'Clock In' : 'Clock Out'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
